/**
 * PACKAGE
 * LOCALIZATION
 * CONSTANTS
 * UTILITY
 * STORE
 * HOC COMPONENTS
 * COMPONENTS
 * ASSETS
 * HELP
 * STYLES
 */

import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';

// CONSTANTS
import { siteUrl } from '@/constants/common';
import { ROUTE_TV, ROUTE_PARTNER_TV } from '@/constants/routes';

// UTILITY
import { analyticsPageLoaded } from '@/utility/analytics';
import { getS3ImageUrl } from '@/utility/content';
import { getLink, scrollToSection } from '@/utility/routes';

// STORE
import { AppActions } from '@/store/actions';

// HOC
import Guard from '@/hoc/Guard';

// COMPONENTS
import AppSection from '@/components/AppSection';
import SellInternetSection from '@/components/SellInternetSection';

// HELP
import Welcome from './help/Welcome';
import ShowsAndMovies from './help/ShowsAndMovies';
import ACPQualification from './help/ACPQualification';
import StreamingMarketplace from './help/StreamingMarketplace';
import FreeTVSection from '@/components/FreeTVSection/FreeTVSection';
import FAQ from '@/components/FAQ/FAQ';

// STYLES
import { Container } from './styles';
import useActions from '@/hooks/useActions';

const defaultOGImg = getS3ImageUrl('/og-image/og-default.png');

const PartnerTv = () => {
  const [t] = useTranslation();

  const { partner, partnerLoading } = useSelector(({ partner: partnerStore }) => ({
    partner: partnerStore.data,
    partnerLoading: partnerStore.loadings.partner,
  }));

  const [healthCheck] = useActions([AppActions.healthCheck.action]);

  useEffect(() => {
    healthCheck();

    scrollToSection();

    window.prerendercloudReady = false;
  }, []);

  const handleChangeHelmetData = () => {
    analyticsPageLoaded();

    window.prerendercloudReady = true;
  };

  const isPartner = !!partner.slug;
  const textOverwrites = partner.text_overwrites || {};

  const faqData = [
    {
      title: textOverwrites.tv_question1_question || t('partnerTV.questions.question1.question'),
      text: textOverwrites.tv_question1_answer || t('partnerTV.questions.question1.answer'),
    },
    {
      title: textOverwrites.tv_question2_question || t('partnerTV.questions.question2.question'),
      text: textOverwrites.tv_question2_answer || t('partnerTV.questions.question2.answer'),
    },
    {
      title: textOverwrites.tv_question3_question || t('partnerTV.questions.question3.question'),
      text: textOverwrites.tv_question3_answer || t('partnerTV.questions.question3.answer'),
    },
    {
      title: textOverwrites.tv_question4_question || t('partnerTV.questions.question4.question'),
      text: textOverwrites.tv_question4_answer || t('partnerTV.questions.question4.answer'),
    },
    {
      title: textOverwrites.tv_question5_question || t('partnerTV.questions.question5.question'),
      text: textOverwrites.tv_question5_answer || t('partnerTV.questions.question5.answer'),
    },
    {
      title: textOverwrites.tv_question6_question || t('partnerTV.questions.question6.question'),
      text: textOverwrites.tv_question6_answer || t('partnerTV.questions.question6.answer'),
    },
  ];

  const seoData = {
    canonicalUrl: `${siteUrl}${ROUTE_TV}`,
    ogUrl: isPartner
      ? `${siteUrl}${getLink(ROUTE_PARTNER_TV, { partner: partner.slug })}`
      : `${siteUrl}${ROUTE_TV}`,
    title: partner?.meta?.title || 'Find Your Streaming Service - MyBundle',
    ogTitle: partner?.meta?.title || 'MyBundle – Streaming TV advice for YOUR needs',
    description: partner?.meta?.description || "Stop wasting money on the TV you don't watch.",
    image: partner?.meta?.image || defaultOGImg,
  };

  return (
    <Container>
      {!partnerLoading && (
        <Helmet onChangeClientState={handleChangeHelmetData}>
          <title>{seoData.title}</title>
          <meta property="og:title" content={seoData.ogTitle} />
          <meta property="og:site_name" content="MyBundle" />
          <link rel="canonical" href={seoData.canonicalUrl} />
          <meta property="og:url" content={seoData.ogUrl} />
          <meta property="og:description" content={seoData.description} />
          <meta name="description" content={seoData.description} />
          <meta property="og:type" content="website" />
          <meta property="fb:app_id" content="608514030003288" />
          <meta property="og:image" content={seoData.image} />
          <meta name="robots" content="index, follow, max-image-preview:large" />
          <meta name="twitter:title" content={seoData.ogTitle} />
          <meta name="twitter:description" content={seoData.description} />
          <meta name="twitter:image" content={seoData.image} />
          <meta name="twitter:site" content="@mybundletv" />
          <meta name="twitter:creator" content="@mybundletv" />
          <meta name="twitter:card" content="summary" />
        </Helmet>
      )}
      <div className="partner-home-page">
        <Welcome />
        <ACPQualification partner={partner} />
        <ShowsAndMovies />
        {!partnerLoading && (
          <Guard partner={{ use_marketplace: true }}>
            <StreamingMarketplace partner={partner} />
          </Guard>
        )}
        {!partnerLoading && (
          <Guard partner={{ search_movies_shows: true, use_authorizing: true }}>
            <AppSection
              title={textOverwrites.app_section_title || t('partnerTV.appSection.title')}
              subtitle={textOverwrites.app_section_subtitle || t('partnerTV.appSection.subtitle')}
            />
          </Guard>
        )}
        <FreeTVSection />
        <FAQ
          className="section--grey"
          title={t('partnerTV.questions.title')}
          data={faqData}
          containerProps={{ id: 'faq' }}
        />
        <SellInternetSection />
      </div>
    </Container>
  );
};

export default React.memo(PartnerTv);
